"use client";

import React, { useCallback, useEffect, useState, FormEvent, Suspense } from "react";
import { useQueryParam } from "@/lib/routing";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  CardFooter,
} from "@/app/components/ui/card";
import { Label } from "@/app/components/ui/label";
import { Button } from "@/app/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/app/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/app/components/ui/tabs";
import { Slider } from "@/app/components/ui/slider";
import { Loader2 as LoaderIcon, Download as DownloadIcon, Image as ImageIcon, FileType, Zap, Music, Video, FileText } from "lucide-react";
import { toast } from "react-hot-toast";
import FileUpload from "@/app/components/FileUpload";
import { mediaAPI } from "@/lib/media-api";
import { ErrorBoundary } from "@/app/components/ErrorBoundary";
import { ConversionErrorBoundary } from "@/app/components/ConversionErrorBoundary";
import { UploadErrorBoundary } from "@/app/components/UploadErrorBoundary";
import { trpc } from "@/lib/trpc";

type ImageFormat = "png" | "jpg" | "jpeg" | "webp" | "gif" | "bmp" | "tiff" | "avif";
const imageFormats: ImageFormat[] = ["png", "jpg", "jpeg", "webp", "gif", "bmp", "tiff", "avif"];

async function uploadFileToTemp(file: File): Promise<{ path: string }> {
  const formData = new FormData();
  formData.append("file", file);
  const response = await fetch("/api/upload", { method: "POST", body: formData });
  if (!response.ok) throw new Error("File upload failed");
  const result = await response.json();
  return { path: result.path };
}

async function downloadFileToTemp(url: string): Promise<{ path: string }> {
  const response = await fetch("/api/upload", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ uploadThingUrl: url }),
  });
  if (!response.ok) throw new Error("Failed to download file from URL");
  const result = await response.json();
  return { path: result.path };
}

const CONVERT_TABS = ["image", "vert"] as const;
type ConvertTabValue = typeof CONVERT_TABS[number];

function ConvertPageContent() {
  const [file, setFile] = useState<File | null>(null);
  const [uploadedUrl, setUploadedUrl] = useState<string | null>(null);
  const [format, setFormat] = useState<ImageFormat | "">("");
  const [quality, setQuality] = useState(80);
  const [isConverting, setIsConverting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [outputUrl, setOutputUrl] = useState<string | null>(null);
  const [outputName, setOutputName] = useState<string | null>(null);
  
  // Single-param state for `tab` with type safety
  const [activeTab, setActiveTab] = useQueryParam<ConvertTabValue>("tab", {
    parse: (raw) => (raw ?? "image") as ConvertTabValue,
    serialize: (v) => v ?? null,
    defaultValue: "image",
    replace: true, // keep history clean while switching tabs
  });
  const [vertFormats, setVertFormats] = useState<{ from: string[]; to: string[] }>({ from: [], to: [] });
  const [selectedVertFormat, setSelectedVertFormat] = useState<string>("");
  const [vertCategory, setVertCategory] = useState<"image" | "audio" | "video" | "document">("image");

  // TRPC queries for VERT
  const { data: vertAvailable, isLoading: vertLoading } = trpc.vert.isAvailable.useQuery();
  const { data: supportedFormats } = trpc.vert.getSupportedFormats.useQuery(undefined, {
    enabled: !!vertAvailable,
  });
  const vertMutation = trpc.vert.convertFile.useMutation();


  // Update VERT formats when data is available
  useEffect(() => {
    if (supportedFormats) {
      setVertFormats(supportedFormats);
    }
  }, [supportedFormats]);

  // File type categorization
  const fileTypeCategories = {
    image: ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'tiff', 'webp', 'svg', 'ico', 'heic', 'heif', 'avif', 'jxl'],
    audio: ['mp3', 'wav', 'flac', 'aac', 'ogg', 'wma', 'm4a', 'opus', 'aiff', 'au'],
    video: ['mp4', 'avi', 'mov', 'wmv', 'flv', 'webm', 'mkv', 'm4v', '3gp', 'ogv', 'mts', 'm2ts'],
    document: ['pdf', 'doc', 'docx', 'txt', 'rtf', 'odt', 'pages', 'xls', 'xlsx', 'ppt', 'pptx', 'odp', 'ods', 'csv', 'xml', 'html', 'epub', 'mobi', 'azw', 'azw3']
  };

  // Get formats for current category
  const getFormatsForCategory = (category: keyof typeof fileTypeCategories) => {
    if (!vertFormats.to) return [];
    return vertFormats.to.filter(format => 
      fileTypeCategories[category].includes(format.toLowerCase())
    ).sort();
  };

  // Reset selected format when category changes
  const onCategoryChange = (category: "image" | "audio" | "video" | "document") => {
    setVertCategory(category);
    setSelectedVertFormat("");
    setError(null);
  };

  // Image conversion will use mediaAPI instead of TRPC

  const onFileChange = (selectedFile: File | null) => {
    setFile(selectedFile);
    setError(null);
    setOutputUrl(null);
    setOutputName(null);
    setFormat("");
  };

  const onUploadComplete = (fileUrl: string) => {
    setUploadedUrl(fileUrl);
    setError(null);
    setOutputUrl(null);
    setOutputName(null);
    setFormat("");
  };

  const onFormatChange = (v: string) => {
    setFormat(v as ImageFormat);
    setError(null);
  };

  const onVertFormatChange = (v: string) => {
    setSelectedVertFormat(v);
    setError(null);
  };

  // ConvertX conversion function
  const convertWithVert = useCallback(
    async (e: FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      setError(null);
      
      if (!file && !uploadedUrl) {
        setError("Please choose a file.");
        return;
      }

      if (!selectedVertFormat) {
        setError("Please choose a target format.");
        return;
      }

      setIsConverting(true);
      toast.loading("Converting file with VERT…", { id: "vert-toast" });

      try {
        let fileToConvert: File;
        
        if (uploadedUrl) {
          // Decode the base64 URL first
          let actualUrl: string;
          try {
            actualUrl = atob(uploadedUrl);
          } catch (error) {
            // If it's not base64, use it as is
            actualUrl = uploadedUrl;
          }
          
          console.log('Decoded URL:', actualUrl);
          
          // Check if this is a background removal API URL
          if (actualUrl.includes('/api/background-removal/')) {
            // This is a background removal API URL, we need to fetch the actual image
            const response = await fetch(actualUrl);
            if (!response.ok) {
              throw new Error(`Failed to fetch file from URL: ${response.statusText}`);
            }
            const blob = await response.blob();
            const fileName = `background-removed-${Date.now()}.${blob.type.split('/')[1] || 'png'}`;
            fileToConvert = new File([blob], fileName, { type: blob.type });
          } else {
            // Regular file URL
            const response = await fetch(actualUrl);
            if (!response.ok) {
              throw new Error(`Failed to fetch file from URL: ${response.statusText}`);
            }
            const blob = await response.blob();
            const fileName = actualUrl.split('/').pop() || 'file';
            fileToConvert = new File([blob], fileName, { type: blob.type });
          }
        } else if (file) {
          fileToConvert = file;
        } else {
          throw new Error("No file available for conversion");
        }

        // Convert file to base64 using a more reliable method
        const arrayBuffer = await fileToConvert.arrayBuffer();
        const uint8Array = new Uint8Array(arrayBuffer);
        let binary = '';
        for (let i = 0; i < uint8Array.length; i++) {
          binary += String.fromCharCode(uint8Array[i]);
        }
        const base64 = btoa(binary);

        console.log('VERT conversion request:', {
          fileName: fileToConvert.name,
          mimeType: fileToConvert.type,
          to: selectedVertFormat,
          quality: quality,
          fileSize: fileToConvert.size
        });

        // Use TRPC to convert with VERT
        const result = await vertMutation.mutateAsync({
          fileData: base64,
          fileName: fileToConvert.name,
          mimeType: fileToConvert.type,
          to: selectedVertFormat,
          quality: quality,
        });

        if (result.success && result.fileUrl) {
          setOutputUrl(result.fileUrl);
          setOutputName(result.filename || `converted.${selectedVertFormat}`);
          toast.success("Conversion completed ✔", { id: "vert-toast" });
        } else {
          throw new Error("Conversion failed");
        }
      } catch (error: any) {
        const message = error?.message || "VERT conversion failed";
        setError(message);
        toast.error(message, { id: "vert-toast" });
      } finally {
        setIsConverting(false);
      }
    },
    [file, uploadedUrl, selectedVertFormat, quality, vertMutation]
  );

  const convert = useCallback(
    async (e: FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      setError(null);
      if (!file && !uploadedUrl) {
        setError("Please choose a file.");
        return;
      }

      setIsConverting(true);
      toast.loading("Converting image…", { id: "convert-toast" });

      try {
        let filePath: string;
        if (uploadedUrl) {
          const res = await downloadFileToTemp(uploadedUrl);
          filePath = res.path;
        } else if (file) {
          const res = await uploadFileToTemp(file);
          filePath = res.path;
        } else {
          throw new Error("No file available for conversion");
        }

        if (!format) {
          setError("Please choose a target format.");
          return;
        }

        const result = await mediaAPI.convertImage({ filePath, format, quality });

        if (outputUrl) URL.revokeObjectURL(outputUrl);

        if ("url" in result) {
          setOutputUrl(result.url ?? null);
          setOutputName(result.filename ?? null);
          toast.success("Conversion completed ✔", { id: "convert-toast" });
          return;
        }

        if ("buffer" in result) {
          const base64Data = result.buffer.includes(',') ? result.buffer.split(',')[1] : result.buffer;
          const binaryString = atob(base64Data);
          const bytes = new Uint8Array(binaryString.length);
          for (let i = 0; i < binaryString.length; i++) bytes[i] = binaryString.charCodeAt(i);
          const mimeType = result.mimeType || 'image/png';
          const blob = new Blob([bytes], { type: mimeType });
          const url = URL.createObjectURL(blob);
          setOutputUrl(url);
          setOutputName(result.filename ?? null);
          toast.success("Conversion completed ✔", { id: "convert-toast" });
          return;
        }

        throw new Error("No valid result data received from conversion");
      } catch (error: any) {
        const message = error?.message || "Operation failed";
        setError(message);
        toast.error(message, { id: "convert-toast" });
      } finally {
        setIsConverting(false);
      }
    },
    [file, uploadedUrl, format, quality, outputUrl]
  );

  useEffect(() => () => { if (outputUrl) URL.revokeObjectURL(outputUrl); }, [outputUrl]);

  // Suppress noisy external script errors (args.site.enabledFeatures undefined)
  useEffect(() => {
    const handleRejection = (e: PromiseRejectionEvent) => {
      try {
        const reason: any = e.reason;
        const msg = typeof reason === 'string' ? reason : reason?.message;
        if (msg && (msg.includes('enabledFeatures') || msg.includes('args.site'))) {
          e.preventDefault();
        }
      } catch {}
    };
    const handleError = (e: ErrorEvent) => {
      const msg = e.message || '';
      if (msg.includes('enabledFeatures') || msg.includes('args.site')) {
        e.preventDefault();
      }
    };
    window.addEventListener('unhandledrejection', handleRejection);
    window.addEventListener('error', handleError, true);
    return () => {
      window.removeEventListener('unhandledrejection', handleRejection);
      window.removeEventListener('error', handleError, true as any);
    };
  }, []);

  return (
    <div className="flex min-h-screen items-center justify-center p-4 animate-fade-in">
      <Card className="w-full max-w-2xl card-enhanced">
        <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as ConvertTabValue)}>
          <TabsList className="grid w-full grid-cols-2 mb-4">
            <TabsTrigger value="image">
              <ImageIcon className="mr-2 h-4 w-4" /> Image
            </TabsTrigger>
            <TabsTrigger value="vert" disabled={!vertAvailable}>
              <Zap className="mr-2 h-4 w-4" /> 
              VERT {vertLoading && <LoaderIcon className="ml-1 h-3 w-3 animate-spin" />}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="image">
            <form onSubmit={convert}>
              <CardHeader>
                <CardTitle>Image Converter</CardTitle>
                <CardDescription>
                  Convert PNG, JPG, WEBP, GIF, BMP, TIFF & AVIF with quality control.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <FileUpload
                  id="img-file"
                  label="Upload image"
                  accept={"image/*"}
                  onFileChangeAction={onFileChange}
                  onUploadComplete={(url) => onUploadComplete(url as any)}
                  disabled={isConverting}
                  file={file}
                  maxSize={50}
                  placeholder="Choose an image file or drag it here"
                />
                <div className="space-y-2">
                  <Label htmlFor="img-format">Convert to</Label>
                  <Select value={format} onValueChange={onFormatChange} disabled={isConverting}>
                    <SelectTrigger id="img-format">
                      <SelectValue placeholder="Select format" />
                    </SelectTrigger>
                    <SelectContent>
                      {imageFormats.map((f) => (
                        <SelectItem key={f} value={f}>
                          {f.toUpperCase()}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2 py-1 pb-4">
                  <Label>Quality: {quality}%</Label>
                  <Slider
                    value={[quality]}
                    onValueChange={(value) => setQuality(value[0])}
                    max={100}
                    min={1}
                    step={1}
                    disabled={isConverting}
                    className="w-full"
                  />
                </div>
              </CardContent>
              <CardFooter className="flex flex-col gap-4">
                {error && <p className="text-sm text-red-600 dark:text-red-400">{error}</p>}
                <Button type="submit" disabled={!file || !format || isConverting} className="w-full btn-enhanced hover-lift">
                  {isConverting && <LoaderIcon className="mr-2 h-4 w-4 animate-spin" />}
                  Convert Image
                </Button>
                {outputUrl && (
                  <>
                    <Button variant="outline" asChild className="w-full hover-lift">
                      <a href={outputUrl} download={outputName ?? "image"}>
                        <DownloadIcon className="mr-2 h-4 w-4" />
                        Download
                      </a>
                    </Button>
                    <Label>Preview</Label>
                    <img src={outputUrl} alt="preview" className="mt-1 w-full rounded border animate-scale-in" />
                  </>
                )}
              </CardFooter>
            </form>
          </TabsContent>

          <TabsContent value="vert">
            <form onSubmit={convertWithVert}>
              <CardHeader>
                <CardTitle>VERT File Converter</CardTitle>
                <CardDescription>
                  {vertAvailable 
                    ? `Convert between ${vertFormats.from.length}+ input formats and ${vertFormats.to.length}+ output formats. Choose a category below to see available formats.`
                    : "VERT service is not available or not configured. Please check your VERT service configuration."
                  }
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <FileUpload
                  id="vert-file"
                  label="Upload file"
                  accept="*/*"
                  onFileChangeAction={onFileChange}
                  onUploadComplete={(url) => onUploadComplete(url as any)}
                  disabled={isConverting || !vertAvailable}
                  file={file}
                  maxSize={100}
                  placeholder="Choose any file to convert"
                />
                
                {vertAvailable && (
                  <>
                    <div className="space-y-2">
                      <Label>File Type Category</Label>
                      <Tabs value={vertCategory} onValueChange={(value) => onCategoryChange(value as "image" | "audio" | "video" | "document")}>
                        <TabsList className="grid w-full grid-cols-4">
                          <TabsTrigger value="image">
                            <ImageIcon className="mr-2 h-4 w-4" /> 
                            Image ({getFormatsForCategory('image').length})
                          </TabsTrigger>
                          <TabsTrigger value="audio">
                            <Music className="mr-2 h-4 w-4" /> 
                            Audio ({getFormatsForCategory('audio').length})
                          </TabsTrigger>
                          <TabsTrigger value="video">
                            <Video className="mr-2 h-4 w-4" /> 
                            Video ({getFormatsForCategory('video').length})
                          </TabsTrigger>
                          <TabsTrigger value="document">
                            <FileText className="mr-2 h-4 w-4" /> 
                            Document ({getFormatsForCategory('document').length})
                          </TabsTrigger>
                        </TabsList>
                      </Tabs>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="vert-format">Convert to {vertCategory}</Label>
                      {getFormatsForCategory(vertCategory).length > 0 ? (
                        <Select 
                          value={selectedVertFormat} 
                          onValueChange={onVertFormatChange} 
                          disabled={isConverting}
                        >
                          <SelectTrigger id="vert-format">
                            <SelectValue placeholder={`Select ${vertCategory} format`} />
                          </SelectTrigger>
                          <SelectContent>
                            {getFormatsForCategory(vertCategory).map((format) => (
                              <SelectItem key={format} value={format}>
                                {format.toUpperCase()}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      ) : (
                        <div className="p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
                          <p className="text-sm text-yellow-800 dark:text-yellow-200">
                            No {vertCategory} formats available. Try a different category or check if VERT supports this file type.
                          </p>
                        </div>
                      )}
                    </div>
                  </>
                )}

                {vertAvailable && (
                  <div className="space-y-2 py-1 pb-4">
                    <Label>Quality: {quality}%</Label>
                    <Slider
                      value={[quality]}
                      onValueChange={(value) => setQuality(value[0])}
                      max={100}
                      min={1}
                      step={1}
                      disabled={isConverting}
                      className="w-full"
                    />
                  </div>
                )}

                {!vertAvailable && (
                  <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
                    <p className="text-sm text-yellow-800 dark:text-yellow-200">
                      VERT service is not running. Please start the VERT Docker container to use this feature.
                    </p>
                  </div>
                )}
              </CardContent>
              <CardFooter className="flex flex-col gap-4">
                {error && <p className="text-sm text-red-600 dark:text-red-400">{error}</p>}
                <Button 
                  type="submit" 
                  disabled={!file || !selectedVertFormat || isConverting || !vertAvailable || getFormatsForCategory(vertCategory).length === 0} 
                  className="w-full btn-enhanced hover-lift"
                >
                  {isConverting && <LoaderIcon className="mr-2 h-4 w-4 animate-spin" />}
                  Convert to {selectedVertFormat ? selectedVertFormat.toUpperCase() : vertCategory}
                </Button>
                {outputUrl && (
                  <>
                    <Button variant="outline" asChild className="w-full hover-lift">
                      <a href={outputUrl} download={outputName ?? "converted-file"}>
                        <DownloadIcon className="mr-2 h-4 w-4" />
                        Download
                      </a>
                    </Button>
                    {outputUrl.startsWith('blob:') && (
                      <>
                        <Label>Preview</Label>
                        <img src={outputUrl} alt="preview" className="mt-1 w-full rounded border animate-scale-in" />
                      </>
                    )}
                  </>
                )}
              </CardFooter>
            </form>
          </TabsContent>
        </Tabs>
      </Card>
    </div>
  );
}

export default function ConvertPage() {
  return (
    <ErrorBoundary>
      <ConversionErrorBoundary>
        <UploadErrorBoundary>
          <Suspense fallback={<div className="container mx-auto px-4 py-8">Loading...</div>}>
            <ConvertPageContent />
          </Suspense>
        </UploadErrorBoundary>
      </ConversionErrorBoundary>
    </ErrorBoundary>
  );
}
